import { useState } from "react";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export const NewsletterCTA = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter a valid email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/newsletter/subscribe", {
        email: email.trim(),
        source: "homepage",
      });
      setDone(true);
      toast.success("You're on the list. Welcome to the brief.");
      setEmail("");
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Unable to subscribe. Try again.";
      toast.error(typeof detail === "string" ? detail : "Subscription failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="newsletter"
      className="bg-bb-navy text-white relative overflow-hidden"
      data-testid="newsletter-section"
    >
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.4) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
      </div>

      <div className="bb-container relative py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <div className="bb-overline text-bb-copper">
              The Morning Brief · Weekdays · 06:30 ET
            </div>
            <h2
              className="bb-headline mt-5 text-4xl sm:text-5xl lg:text-6xl text-white"
              data-testid="newsletter-headline"
            >
              The Canadian
              <br />
              mining day,
              <br />
              <span className="text-bb-copper">distilled.</span>
            </h2>
            <p
              className="mt-6 text-base lg:text-lg text-white/75 max-w-xl leading-relaxed"
              data-testid="newsletter-subheadline"
            >
              Project moves, commodity action, policy shifts, and the capital
              markets context Canadian operators actually need. Five minutes,
              before the TSX opens.
            </p>

            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/15 border border-white/15">
              {[
                "Curated by full-time editors",
                "Independent, ad-light",
                "Free during open beta",
              ].map((b) => (
                <li
                  key={b}
                  className="bg-bb-navy p-4 text-sm font-mono text-white/85"
                >
                  {b}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5 w-full">
            <form
              onSubmit={submit}
              className="bg-white p-7 lg:p-8 border border-white/10"
              data-testid="newsletter-form"
            >
              <label className="bb-overline text-bb-slate block mb-3">
                Subscribe — Free
              </label>
              <div className="flex flex-col gap-3">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@firm.ca"
                  className="w-full border border-bb-navy/25 px-4 py-3.5 text-bb-navy font-mono text-sm focus:outline-none focus:border-bb-navy bg-bb-offwhite"
                  data-testid="newsletter-email-input"
                  disabled={loading || done}
                />
                <button
                  type="submit"
                  disabled={loading || done}
                  className="bb-btn-primary inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm disabled:opacity-70"
                  data-testid="newsletter-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Subscribing
                    </>
                  ) : done ? (
                    <>
                      <CheckCircle2 size={16} />
                      Subscribed
                    </>
                  ) : (
                    <>
                      Subscribe to the Brief
                      <ArrowRight size={16} strokeWidth={2.4} />
                    </>
                  )}
                </button>
              </div>
              <p
                className="mt-4 text-xs text-bb-slate font-mono leading-relaxed"
                data-testid="newsletter-disclaimer"
              >
                By subscribing, you agree to receive the BedrockBulletin
                Morning Brief. Unsubscribe anytime. We do not share your data.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterCTA;
